import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * Splits text into visual lines by measuring word spans, then animates each line in.
 * Respects the font metrics (re-measures after custom fonts load).
 */
export default function AnimatedLines({
  text,
  tailItems = [],
  className = "",
  style = {},
  delayMs = 3000,
  lineStaggerMs = 700,
}) {
  const measureRef = useRef(null);
  const [lines, setLines] = useState([]);

  // Prevent breaking on standard "-" (use non-breaking hyphen).
  const normalizedText = useMemo(() => text.replace(/-/g, "\u2011"), [text]);
  const words = useMemo(() => normalizedText.trim().split(/\s+/), [normalizedText]);

  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const container = measureRef.current;

    const compute = () => {
      const spans = Array.from(container.querySelectorAll("[data-word]"));
      const grouped = [];
      let topRef = null;
      let line = [];

      spans.forEach((s) => {
        const t = s.offsetTop;
        if (topRef === null || Math.abs(t - topRef) <= 1) {
          line.push(s.textContent);
          topRef = t;
        } else {
          grouped.push(line.join(" "));
          line = [s.textContent];
          topRef = t;
        }
      });

      if (line.length) grouped.push(line.join(" "));
      setLines(grouped);
    };

    compute();

    const ro = new ResizeObserver(() => requestAnimationFrame(compute));
    ro.observe(container);

    // Re-measure after brand fonts load (avoid fallback metrics)
    let cleanupFonts;
    if (document.fonts && document.fonts.ready) {
      const redo = () => requestAnimationFrame(compute);
      document.fonts.ready.then(redo);
      document.fonts.addEventListener?.("loadingdone", redo);
      cleanupFonts = () => document.fonts.removeEventListener?.("loadingdone", redo);
    }

    return () => {
      ro.disconnect();
      cleanupFonts?.();
    };
  }, [words]);

  return (
    <div className={`animated-block ${className}`} style={style}>
      {/* Hidden measuring line that forces layout */}
      <p ref={measureRef} className="line-measure" aria-hidden="true">
        {words.map((w, i) => (
          <span key={i} data-word>
            {w + (i === words.length - 1 ? "" : " ")}
          </span>
        ))}
      </p>

      {/* Animated visible lines */}
      {lines.map((line, i) => (
        <div
          key={`line-${i}`}
          className="animated-line"
          style={{ animationDelay: `${delayMs + i * lineStaggerMs}ms` }}
        >
          {line}
        </div>
      ))}

      {/* Tail items (links, address) animated in sequence */}
      {tailItems.map((node, j) => {
        const idx = lines.length + j;
        return (
          <div
            key={`tail-${j}`}
            className="animated-line"
            style={{ animationDelay: `${delayMs + idx * lineStaggerMs}ms` }}
          >
            {node}
          </div>
        );
      })}
    </div>
  );
}
